/**
 * Local storage access.
 *
 * One place to reach `localStorage`, because every caller needs the same two
 * guarantees: it may be missing entirely (private windows, blocked site data,
 * sandboxed frames), and keys carry the product name, which changed once.
 */

const PREFIX = 'pixansi:';
/** Keys written before the editor was named. Read once, then migrated. */
const LEGACY_PREFIX = 'ansi-studio:';

export function storage(): Storage | null {
  try {
    const s = window.localStorage;
    const probe = `${PREFIX}__probe__`;
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

export function key(name: string): string {
  return `${PREFIX}${name}`;
}

/**
 * Reads a key, falling back to the pre-rename name and moving it across.
 *
 * Without this, renaming the product would silently orphan everything a user
 * had already saved.
 */
export function read(name: string): string | null {
  const s = storage();
  if (!s) return null;
  const current = s.getItem(key(name));
  if (current !== null) return current;
  const legacy = s.getItem(`${LEGACY_PREFIX}${name}`);
  if (legacy === null) return null;
  try {
    s.setItem(key(name), legacy);
    s.removeItem(`${LEGACY_PREFIX}${name}`);
  } catch {
    /* migration is best-effort; the value is still returned */
  }
  return legacy;
}

export function write(name: string, value: string): boolean {
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(key(name), value);
    return true;
  } catch {
    return false;
  }
}

export function remove(name: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(key(name));
    s.removeItem(`${LEGACY_PREFIX}${name}`);
  } catch {
    /* nothing to do */
  }
}
